import PropTypes from 'prop-types';
import { useMemo } from 'react';

import { closestCenter } from '@dnd-kit/core';

import { DndSortableList, removeScaleY, useSortableDndListItem } from '@/components/SortableDnd';

import { DroppableArea } from '../Draggable';
import Droppable from '../Droppable';
import SortablePages from '../SortablePages';

import Header from './Header';

const slotSortableIds = new WeakMap();
let slotSortableIdSeq = 0;

function sortableIdForSlot(slot) {
  if (!slotSortableIds.has(slot)) {
    slotSortableIds.set(slot, `slot-sort-${++slotSortableIdSeq}`);
  }
  return slotSortableIds.get(slot);
}

function SortableSlotWrapper({ children, sortableId, onMoveSlot, slotIndex }) {
  const dnd = useSortableDndListItem(sortableId)
  const style = removeScaleY(dnd.style)
  return <>
    <DroppableArea origin="slot" onMove={onMoveSlot} node={{ slotIndex }} type="slot-item" />
    <div ref={dnd.setNodeRef} style={style}>
      {children}
    </div>
  </>
}

const propTypes = {
  pages: PropTypes.array.any,
  onClick: PropTypes.func,
  onMove: PropTypes.func,
  onDropLast: PropTypes.func,
  onRemove: PropTypes.func,
  onReorderPages: PropTypes.func.isRequired,
  onReorderSlots: PropTypes.func.isRequired,
}

export default function Slots({ onMove, onDropLast, onRemove, onReorderPages, onReorderSlots, slots, onMoveSlot, onDropLastSlot, onRemoveSlot, onChangeSlot, onClick, originalSlotsPositions }) {
  const sortableIds = useMemo(() => slots.map(sortableIdForSlot), [slots])

  if (!slots?.length) {
    return <Droppable tag="div" type="slot-item" onMove={onMove} node={{ index: 0 }}>
      <Droppable tag="div" type="page-item" className="mt-2 p-6 w-full flex items-center justify-center twy-subtle-neutral border rounded twy-text-base-accent border-dashed" onMove={onMoveSlot} node={{ index: 0 }}>
        Drag page here to create a new slot
      </Droppable>
    </Droppable>
  }

  return <>
    <DndSortableList
      items={slots}
      itemIds={sortableIds}
      onSortEnd={onReorderSlots}
      collisionDetection={closestCenter}
      activationConstraint={{ distance: 8 }}
    >
      {slots.map((slot, index) => <SortableSlotWrapper
        key={sortableIds[index]}
        sortableId={sortableIds[index]}
        onMoveSlot={onMoveSlot}
        slotIndex={index}
      >
        <Header
          sortableId={sortableIds[index]}
          index={index}
          slot={slot}
          onChangeSlot={onChangeSlot}
          onRemoveSlot={onRemoveSlot}
          originalSlotsPositions={originalSlotsPositions}
        />
        <SortablePages
          items={slot.pages}
          slotIndex={index}
          onClick={onClick}
          onMove={onMove}
          onDropLast={onDropLast}
          onRemove={onRemove}
          onReorder={pages => onReorderPages(index, pages)}
          totalSlotImpression={slot.impressions}
          onChangeOptimize={optimize => onChangeSlot({ data: optimize, key: "optimize", slotIndex: index })}
          optimize={slot.optimize || {}}
        />
      </SortableSlotWrapper>)}
    </DndSortableList>
    <DroppableArea origin="slot" onMove={onDropLastSlot} node={{ slotIndex: slots.length - 1 }} />
  </>
}

Slots.propTypes = propTypes;
